import React from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { classNames } from '../../utils/classNames';

/**
 * FL-113: 車検状態表示コンポーネント
 * 案件の車検状態を視覚的に表示するための統一コンポーネント
 */

export interface VehicleInspectionStatusProps {
  /** 車検切れフラグ */
  isExpired?: boolean;
  /** 仮ナンバー要否 */
  requiresTemporaryNumber?: boolean;
  /** 仮ナンバー関連備考 */
  temporaryNumberNotes?: string;
  /** 表示サイズ */
  size?: 'small' | 'medium' | 'large';
  /** 表示形式 */
  variant?: 'badge' | 'inline' | 'detailed';
  /** 追加のクラス名 */
  className?: string;
}

/**
 * 車検状態を表示するコンポーネント
 * ダークモード対応・アクセシビリティ対応済み
 */
export const VehicleInspectionStatus: React.FC<VehicleInspectionStatusProps> = ({
  isExpired = false,
  requiresTemporaryNumber = false,
  temporaryNumberNotes,
  size = 'medium',
  variant = 'badge',
  className = '',
}) => {
  // サイズに応じたスタイル設定
  const sizeStyles = {
    small: {
      icon: 'h-3 w-3',
      text: 'text-xs',
      padding: 'px-2 py-0.5',
      gap: 'gap-1',
    },
    medium: {
      icon: 'h-4 w-4',
      text: 'text-sm',
      padding: 'px-3 py-1',
      gap: 'gap-1.5',
    },
    large: {
      icon: 'h-5 w-5',
      text: 'text-base',
      padding: 'px-4 py-2',
      gap: 'gap-2',
    },
  };

  const currentSize = sizeStyles[size];

  // 車検状態の判定とスタイル設定
  const getStatusInfo = () => {
    if (requiresTemporaryNumber) {
      return {
        icon: DocumentTextIcon,
        label: '仮ナンバー必要',
        colorClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        borderClass: 'border-yellow-300 dark:border-yellow-700',
        priority: 1,
      };
    }
    if (isExpired) {
      return {
        icon: ExclamationTriangleIcon,
        label: '車検切れ',
        colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        borderClass: 'border-red-300 dark:border-red-700',
        priority: 2,
      };
    }
    return {
      icon: CheckCircleIcon,
      label: '車検有効',
      colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      borderClass: 'border-green-300 dark:border-green-700',
      priority: 3,
    };
  };

  const statusInfo = getStatusInfo();
  const Icon = statusInfo.icon;

  // バッジスタイルのレンダリング
  if (variant === 'badge') {
    return (
      <span
        className={classNames(
          'inline-flex items-center rounded-full font-medium border',
          currentSize.padding,
          currentSize.gap,
          statusInfo.colorClass,
          statusInfo.borderClass,
          className
        )}
        aria-label={`車検状態: ${statusInfo.label}`}
      >
        <Icon className={currentSize.icon} aria-hidden='true' />
        <span className={currentSize.text}>{statusInfo.label}</span>
      </span>
    );
  }

  // インラインスタイルのレンダリング
  if (variant === 'inline') {
    return (
      <span
        className={classNames(
          'inline-flex items-center',
          currentSize.gap,
          currentSize.text,
          statusInfo.colorClass.replace('bg-', '').replace('/30', ''),
          className
        )}
        aria-label={`車検状態: ${statusInfo.label}`}
      >
        <Icon className={currentSize.icon} aria-hidden='true' />
        <span>{statusInfo.label}</span>
      </span>
    );
  }

  // 詳細スタイルのレンダリング
  if (variant === 'detailed') {
    return (
      <div
        className={classNames(
          'rounded-lg border p-3 sm:p-4',
          statusInfo.colorClass,
          statusInfo.borderClass,
          className
        )}
        role='status'
        aria-label={`車検状態: ${statusInfo.label}`}
      >
        <div className={classNames('flex items-start', currentSize.gap)}>
          <Icon
            className={classNames(currentSize.icon, 'mt-0.5 flex-shrink-0')}
            aria-hidden='true'
          />
          <div className='flex-1'>
            <p className={classNames(currentSize.text, 'font-medium')}>{statusInfo.label}</p>
            {temporaryNumberNotes && (
              <p
                className={classNames(
                  'mt-1 text-gray-600 dark:text-gray-400',
                  size === 'small' ? 'text-xs' : 'text-sm'
                )}
              >
                {temporaryNumberNotes}
              </p>
            )}
            {requiresTemporaryNumber && !temporaryNumberNotes && (
              <p
                className={classNames(
                  'mt-1 text-gray-600 dark:text-gray-400',
                  size === 'small' ? 'text-xs' : 'text-sm'
                )}
              >
                仮ナンバーの申請が必要です。申請状況を確認してください。
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// デフォルトエクスポート
export default VehicleInspectionStatus;
